import { useMemo, useState, type ReactElement } from 'react';
import { createDefaultCabinConfiguration } from '../../domain/economics';
import type { AircraftType, AircraftTypeId, Registration } from '../../domain/models';
import { AircraftSelector } from './cabin/AircraftSelector';
import { CabinWorkbench } from './cabin/CabinWorkbench';
import type {
  AcquisitionMode,
  CabinApplyTarget,
  CabinEditorCommand,
  EditorSourceKind,
  FleetCabinTarget,
  SavedCabinPreset,
} from './cabin/cabinEditorTypes';
import { useCabinPresets } from './cabin/useCabinPresets';
import './CabinEditor.css';

export interface CabinEditorProps {
  /** Every aircraft type that can be acquired (and that fleet aircraft refer to). */
  readonly aircraftTypes: readonly AircraftType[];
  /** Aircraft already owned, with their installed cabins. */
  readonly fleet?: readonly FleetCabinTarget[];
  /** Type preselected for a pending acquisition. Defaults to the first type. */
  readonly initialTypeId?: AircraftTypeId;
  /** When set (and found in `fleet`), the editor opens on that fleet aircraft. */
  readonly initialRegistration?: Registration;
  /** Receives commands for the simulation worker (via the host's worker client). */
  readonly onCommand: (command: CabinEditorCommand) => void;
  /** Called after the player saves a preset; the host may mirror it elsewhere. */
  readonly onPresetSaved?: (preset: SavedCabinPreset) => void;
  readonly className?: string;
}

const NO_FLEET: readonly FleetCabinTarget[] = [];

/**
 * Interactive cabin layout and seat configuration editor.
 *
 * The component owns only presentation state. Metrics and validation come from
 * the pure domain functions in `domain/economics`; results leave as commands
 * through `onCommand`, so this file never talks to the Web Worker itself.
 */
export function CabinEditor({
  aircraftTypes,
  fleet = NO_FLEET,
  initialTypeId,
  initialRegistration,
  onCommand,
  onPresetSaved,
  className,
}: CabinEditorProps): ReactElement {
  const presetStore = useCabinPresets();

  const typeById = useMemo(
    () => new Map<AircraftTypeId, AircraftType>(aircraftTypes.map((type) => [type.id, type])),
    [aircraftTypes]
  );
  const fleetByRegistration = useMemo(
    () => new Map<Registration, FleetCabinTarget>(fleet.map((aircraft) => [aircraft.registration, aircraft])),
    [fleet]
  );

  const [sourceKind, setSourceKind] = useState<EditorSourceKind>(() =>
    initialRegistration !== undefined && fleetByRegistration.has(initialRegistration)
      ? 'FLEET'
      : 'ACQUISITION'
  );
  const [typeId, setTypeId] = useState<AircraftTypeId>(
    () => initialTypeId ?? aircraftTypes[0]?.id ?? ''
  );
  const [registration, setRegistration] = useState<Registration | null>(
    () => initialRegistration ?? fleet[0]?.registration ?? null
  );
  const [acquisitionMode, setAcquisitionMode] = useState<AcquisitionMode>('OPERATING_LEASE');

  const rootClass = className === undefined ? 'cabin-editor' : `cabin-editor ${className}`;

  if (aircraftTypes.length === 0) {
    return (
      <div className={rootClass}>
        <p className="ce-empty" role="status">
          No aircraft types are loaded yet, so there is no cabin to edit.
        </p>
      </div>
    );
  }

  const fleetAircraft = registration === null ? undefined : fleetByRegistration.get(registration);
  const fleetType = fleetAircraft === undefined ? undefined : typeById.get(fleetAircraft.typeId);
  const editingFleet = sourceKind === 'FLEET' && fleetAircraft !== undefined && fleetType !== undefined;

  const acquisitionType = typeById.get(typeId) ?? aircraftTypes[0];
  const activeType = editingFleet ? fleetType : acquisitionType;

  if (activeType === undefined) {
    return (
      <div className={rootClass}>
        <p className="ce-empty" role="status">
          The selected aircraft type could not be found.
        </p>
      </div>
    );
  }

  const target: CabinApplyTarget =
    editingFleet && fleetAircraft !== undefined
      ? { kind: 'FLEET', registration: fleetAircraft.registration }
      : { kind: 'ACQUISITION', typeId: activeType.id, mode: acquisitionMode };

  const workbenchKey =
    target.kind === 'FLEET' ? `fleet:${target.registration}` : `acquire:${activeType.id}`;
  const baseline =
    editingFleet && fleetAircraft !== undefined
      ? fleetAircraft.cabin
      : createDefaultCabinConfiguration(activeType);

  return (
    <div className={rootClass}>
      <header className="ce-header">
        <h2 className="ce-title">Cabin layout editor</h2>
        <p className="ce-subtitle">
          Trade seats for comfort within the floor budget. Everything below updates live.
        </p>
      </header>

      <AircraftSelector
        aircraftTypes={aircraftTypes}
        fleet={fleet}
        sourceKind={editingFleet ? 'FLEET' : 'ACQUISITION'}
        typeId={activeType.id}
        registration={registration}
        acquisitionMode={acquisitionMode}
        activeType={activeType}
        onSourceKindChange={setSourceKind}
        onTypeChange={setTypeId}
        onRegistrationChange={setRegistration}
        onAcquisitionModeChange={setAcquisitionMode}
      />

      <CabinWorkbench
        key={workbenchKey}
        type={activeType}
        baseline={baseline}
        target={target}
        presetStore={presetStore}
        onCommand={onCommand}
        {...(onPresetSaved !== undefined ? { onPresetSaved } : {})}
      />
    </div>
  );
}
